/**
 * Die Tagesaufnahme pausiert nur mit Absicht. (27.09.2026, Ticket 2708)
 *
 * Martin, 27.09. gegen 16:48: *„Ich bin jetzt irgendwo angekommen beim
 * Sprechen und dann stand plötzlich … die Aufnahme auf Pause … es soll keine
 * Pause ausgelöst werden, außer wenn etwas abgespielt wird oder wenn ich
 * etwas aufnehme im Push-to-Talk."*
 *
 * Im Journal stand um 16:47:00 `halt-geloest:nutzer-will-aufnehmen` und
 * gleich danach `tagesaufnahme-pause-fremd` — genau der Weg dieses Knopfs,
 * ohne irgendeine Wiedergabe davor. Beim Ankommen reichte eine Berührung
 * (Hand, Tasche), und die Aufnahme stand.
 *
 * Darum pausiert der Knopf nur noch, wenn man ihn HÄLT. Ein kurzer Tipp
 * pausiert nicht, er sagt nur, wie es geht. Fortsetzen bleibt ein Tipp:
 * dort schadet ein Versehen nicht, es nimmt nur weiter auf.
 *
 * ⚠️ Die Haltezeit gehört dieser Datei. Das CSS liest sie über
 *    `--halte-dauer`; der Ring ist nur die Anzeige, ausgelöst wird hier
 *    über den Zeitgeber. So bleibt die Schwelle auch bei „Bewegung
 *    reduzieren" bestehen, wo die App alle Übergänge auf null setzt.
 *
 * Ein Besitzer je Knopf: Beide Pause-Knöpfe der Aufnahme (Karte in „Mein
 * Alltag" und das Aufnahme-Blatt) entstehen hier; Beschriftung, Zustand und
 * Geste stehen nur an dieser Stelle.
 */
import { meldeMikroAnlass } from "./mikro-spur.js";

/** So lange muss der Knopf gehalten werden, bis er pausiert. */
export const HALTE_MS = 500;

/** Was ein kurzer Tipp sagt — und was der Screenreader vorab ansagt. */
export const HALTE_HINWEIS = "Zum Pausieren gedrückt halten";

export type PauseModus = "pause" | "fortsetzen";

interface HaltePauseKnopf {
  knopf: HTMLButtonElement;
  /** „pause" = die Aufnahme läuft (Halten pausiert), „fortsetzen" = sie steht (Tipp). */
  zeige(modus: PauseModus): void;
}

/** Ein Druck, der gerade läuft oder eben endete — sein Klick gehört noch zu ihm. */
interface Druck {
  quelle: "zeiger" | "taste";
  seit: number;
  ausgeloest: boolean;
}

/**
 * `griff` bekommt, was auf dem Knopf stand — nicht „irgendwas umschalten".
 * (Ticket 2708, Nachbesserung) Ein Tipp auf ein veraltetes „Fortsetzen"
 * pausierte sonst eine Aufnahme, die längst wieder lief.
 */
export function baueHaltePauseKnopf(
  klasse: string,
  griff: (absicht: PauseModus) => void,
): HaltePauseKnopf {
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = `${klasse} halte-knopf`;
  knopf.style.setProperty("--halte-dauer", `${HALTE_MS}ms`);

  const ring = document.createElement("span");
  ring.className = "halte-ring";
  ring.setAttribute("aria-hidden", "true");
  const wort = document.createElement("span");
  wort.className = "halte-wort";
  knopf.append(ring, wort);

  let modus: PauseModus = "pause";
  let druck: Druck | null = null;
  let zeitgeber: number | null = null;

  const stoppe = (): void => {
    if (zeitgeber !== null) {
      window.clearTimeout(zeitgeber);
      zeitgeber = null;
    }
    knopf.classList.remove("is-halten");
  };

  const starte = (quelle: Druck["quelle"]): void => {
    stoppe();
    const d: Druck = { quelle, seit: Date.now(), ausgeloest: false };
    druck = d;
    knopf.classList.add("is-halten");
    zeitgeber = window.setTimeout(() => {
      zeitgeber = null;
      knopf.classList.remove("is-halten");
      d.ausgeloest = true;
      griff("pause");
    }, HALTE_MS);
  };

  /**
   * Ein Druck ist vorbei, ohne dass er lange genug war.
   *
   * ⚠️ Der Tipp landet als eigene Zeile im Journal. Nur so zeigt der
   *    nächste Fall, ob eine Berührung abgefangen wurde oder ob die Pause
   *    einen anderen Weg nahm.
   */
  const zuKurz = (d: Druck | null): void => {
    const gehalten = d ? `${Date.now() - d.seit}ms` : "klick";
    meldeMikroAnlass("tagesaufnahme-pause-abgewiesen", `gehalten=${gehalten}`);
    window.dispatchEvent(
      new CustomEvent("voice-v8:toast", { detail: { message: HALTE_HINWEIS, ms: 2500 } }),
    );
  };

  knopf.addEventListener("pointerdown", (e) => {
    druck = null;
    if (modus !== "pause" || !e.isPrimary || e.button !== 0) return;
    starte("zeiger");
  });
  // Loslassen, Abrutschen, Wischen: nur der Zeitgeber endet. Den Druck
  // selbst räumt erst sein Klick — sonst setzte der Klick nach einem
  // gelungenen Halten die eben pausierte Aufnahme gleich wieder fort.
  for (const ende of ["pointerup", "pointerleave", "pointercancel"] as const) {
    knopf.addEventListener(ende, stoppe);
  }
  // Langes Drücken öffnet sonst auf manchen Geräten ein Kontextmenü.
  knopf.addEventListener("contextmenu", (e) => e.preventDefault());

  const istTaste = (e: KeyboardEvent): boolean => e.key === "Enter" || e.key === " ";
  knopf.addEventListener("keydown", (e) => {
    if (!istTaste(e)) return;
    if (modus !== "pause" && druck?.quelle !== "taste") return;
    // Der Klick der Taste gehört dem Halten, nicht dem Knopf.
    e.preventDefault();
    if (e.repeat) return;
    starte("taste");
  });
  knopf.addEventListener("keyup", (e) => {
    if (!istTaste(e) || druck?.quelle !== "taste") return;
    e.preventDefault();
    const d = druck;
    druck = null;
    stoppe();
    if (!d.ausgeloest) zuKurz(d);
  });

  knopf.addEventListener("click", () => {
    if (druck) {
      const d = druck;
      druck = null;
      stoppe();
      if (!d.ausgeloest) zuKurz(d);
      return;
    }
    if (modus === "fortsetzen") {
      griff("fortsetzen");
      return;
    }
    // Ein Klick ohne Druck davor: Screenreader oder Schaltersteuerung.
    zuKurz(null);
  });

  /*
   * ⚠️ Nur ein WECHSEL bricht ein laufendes Halten ab. Derselbe Zustand
   *    kommt oft mehrfach (jeder Abgleich mit der App meldet ihn neu) — ein
   *    Abbruch darauf ließe das Halten mitten im Ring scheitern.
   */
  const zeige = (neu: PauseModus): void => {
    if (neu !== modus) stoppe();
    modus = neu;
    knopf.dataset.modus = neu;
    wort.textContent = neu === "pause" ? "Pause" : "Fortsetzen";
    if (neu === "pause") knopf.setAttribute("aria-description", HALTE_HINWEIS);
    else knopf.removeAttribute("aria-description");
  };
  zeige("pause");

  return { knopf, zeige };
}
